import type { BankTallyMapping } from "./build-bank-tally-vouchers";

export const BANK_FORMATS = ["PNB", "HDFC"] as const;

export type BankFormat = (typeof BANK_FORMATS)[number];

export const BANK_FORMAT_OPTIONS: { value: BankFormat; label: string }[] = [
  { value: "PNB", label: "PNB (Punjab National Bank)" },
  { value: "HDFC", label: "HDFC Bank" },
];

export const BANK_STATEMENT_ACCEPT = ".xls,.xlsx,.csv,application/vnd.ms-excel,text/csv";

export type BankTallyExportFormState = {
  bankFormat: BankFormat;
  file: File | null;
  mapping: BankTallyMapping;
};

export function emptyBankTallyMapping(): BankTallyMapping {
  return {
    drLedger: "",
    crLedger: "",
    costCategory: "",
    costCentre: "",
  };
}

export function defaultBankTallyExportForm(): BankTallyExportFormState {
  return {
    bankFormat: "PNB",
    file: null,
    mapping: emptyBankTallyMapping(),
  };
}

export function isBankFormat(value: string): value is BankFormat {
  return (BANK_FORMATS as readonly string[]).includes(value);
}

/** Accept .xls / .xlsx / .csv by filename (browsers vary on MIME). */
export function isAcceptedBankStatementFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith(".xls") || name.endsWith(".xlsx") || name.endsWith(".csv");
}

export function isBankTallyMappingComplete(mapping: BankTallyMapping): boolean {
  return Boolean(
    mapping.drLedger.trim() &&
    mapping.crLedger.trim() &&
    mapping.costCategory.trim() &&
    mapping.costCentre.trim()
  );
}

/**
 * Returns a user-facing error, or `null` when the form is ready to parse.
 */
export function validateBankTallyExportForm(form: BankTallyExportFormState): string | null {
  if (!isBankFormat(form.bankFormat)) {
    return "Select a bank statement format.";
  }
  if (!form.file) {
    return "Choose a bank statement file (.xls, .xlsx, or .csv).";
  }
  if (!isAcceptedBankStatementFile(form.file)) {
    return "File must be .xls, .xlsx, or .csv.";
  }
  if (!isBankTallyMappingComplete(form.mapping)) {
    return "Dr ledger, Cr ledger, cost category, and cost centre are required.";
  }
  return null;
}
